import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { isGameMaster } from '../lib/utils';
import {
  HistoryTab,
  SheetSection,
  computeCompleteness,
} from '../components/character/CharacterSheetRenderer';
import CharacterPDFMode from '../components/character/CharacterPDFMode';
import {
  Loader2,
  Pencil,
  Save,
  X,
  ClipboardList,
  History,
  FileText,
  Download,
  ChevronLeft,
  Eye,
  EyeOff,
  Shield,
  Users,
} from 'lucide-react';

const API_URL = '/api';

export default function GMCharacterSheetPage() {
  const { campaignId, sheetId } = useParams();
  const navigate                = useNavigate();
  const [searchParams]          = useSearchParams();
  const { user, language }      = useAuth();
  const { currentCampaign }     = useCampaign();

  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [exportingPdf,     setExportingPdf]     = useState(false);
  const [editMode,         setEditMode]         = useState(false);
  const [viewAsPlayer,     setViewAsPlayer]     = useState(false); // Bonus: "voir comme joueur"
  const [activeTab,        setActiveTab]        = useState(searchParams.get('tab') || 'sheet');

  const [sheet,            setSheet]            = useState(null);
  const [template,         setTemplate]         = useState(null);
  const [formData,         setFormData]         = useState({});
  const [memberInfo,       setMemberInfo]       = useState(null);

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ     = isGameMaster(userRole);

  // ── Tous les hooks en premier (avant tout return conditionnel) ────────

  const loadData = useCallback(async () => {
    if (!campaignId || !sheetId) return;
    setLoading(true);
    try {
      const sheetRes = await axios.get(
        `${API_URL}/campaigns/${campaignId}/character-sheets/${sheetId}`
      );
      const s = sheetRes.data;
      setSheet(s);
      setFormData(s.data || {});

      const [tplRes] = await Promise.all([
        axios.get(`${API_URL}/system-templates/${s.template_id}`),
      ]);
      setTemplate(tplRes.data);

      const member = currentCampaign?.members?.find(m => m.user_id === s.user_id);
      setMemberInfo(member || null);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        toast.error(language === 'fr' ? 'Accès refusé à cette fiche' : 'Access denied to this sheet');
        navigate(`/campaigns/${campaignId}/characters`);
      } else if (status === 404) {
        toast.error(language === 'fr' ? 'Fiche introuvable' : 'Sheet not found');
        navigate(`/campaigns/${campaignId}/characters`);
      } else {
        toast.error(err.response?.data?.detail || t('error_generic', language));
      }
    } finally {
      setLoading(false);
    }
  }, [campaignId, sheetId, currentCampaign?.members, language, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Version sync depuis mode PDF ──────────────────────────────────────
  const handlePdfVersionUpdate = useCallback((newVersion) => {
    setSheet(prev => prev ? { ...prev, version: newVersion } : prev);
  }, []);

  // ── Redirection si pas MJ (après tous les hooks) ──────────────────────
  if (!loading && !isMJ) {
    return <Navigate to={`/campaigns/${campaignId}/character-sheet`} replace />;
  }

  // ── Champ change ──────────────────────────────────────────────────────
  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Sauvegarder ──────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API_URL}/campaigns/${campaignId}/character-sheets/${sheetId}`,
        { data: formData, version: sheet.version }
      );
      setSheet(res.data);
      setFormData(res.data.data || {});
      toast.success(t('success_saved', language));
      setEditMode(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        toast.error(language === 'fr'
          ? 'Conflit de version — rechargez la fiche'
          : 'Version conflict — reload the sheet');
        loadData();
      } else {
        toast.error(detail || t('error_generic', language));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData(sheet?.data || {});
    setEditMode(false);
  };

  // ── Export PDF ────────────────────────────────────────────────────────
  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await axios.get(
        `${API_URL}/campaigns/${campaignId}/character-sheets/${sheetId}/export/pdf`,
        { responseType: 'blob' }
      );
      const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a    = document.createElement('a');
      const disp = res.headers['content-disposition'] || '';
      const m    = disp.match(/filename="?([^";\n]+)"?/i);
      a.href     = url;
      a.download = m?.[1] || `sheet_${sheetId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (err) {
      toast.error(
        err.response?.status === 422
          ? t('pdf_mode_no_overlay', language)
          : t('pdf_mode_export_error', language)
      );
    } finally {
      setExportingPdf(false);
    }
  };

  // ── Données dérivées ─────────────────────────────────────────────────
  const playerName = memberInfo?.character_name || memberInfo?.username || sheetId.slice(0, 8);
  const characterName = formData?.character_name || formData?.name || null;

  const hasPdfOverlay = !!(template?.pdf_asset_id && template?.pdf_overlay?.pages?.length > 0);
  const showPdfTab    = hasPdfOverlay;

  // Sections visibles selon viewAsPlayer toggle
  const visibleSections = (template?.layout?.sections || []).filter(section => {
    if (!viewAsPlayer) return true; // MJ voit tout
    return (section.access?.player ?? 'read') !== 'hidden';
  });

  const completeness = template && sheet
    ? computeCompleteness(template.fields_schema, formData)
    : null;

  const isReadOnly = !editMode;

  // ── Chargement ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!sheet || !template) {
    return (
      <MainLayout>
        <div className="max-w-lg mx-auto mt-20 text-center space-y-4">
          <ClipboardList className="h-14 w-14 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">
            {language === 'fr' ? 'Fiche introuvable' : 'Sheet not found'}
          </p>
          <Button variant="outline" onClick={() => navigate(`/campaigns/${campaignId}/characters`)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            {language === 'fr' ? 'Retour à la liste' : 'Back to list'}
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-5 animate-fade-in max-w-3xl mx-auto">

        {/* ── Breadcrumb ──────────────────────────────────────────────────── */}
        <button
          onClick={() => navigate(`/campaigns/${campaignId}/characters`)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
        >
          <ChevronLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          <Users className="h-3.5 w-3.5" />
          {language === 'fr' ? 'Fiches des joueurs' : 'Player Sheets'}
        </button>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="font-cinzel text-2xl font-bold flex items-center gap-2">
              <ClipboardList className="h-6 w-6" />
              {characterName || playerName}
            </h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="secondary" className="text-xs gap-1">
                <Shield className="h-3 w-3" />
                {language === 'fr' ? 'Vue MJ' : 'GM View'}
              </Badge>
              {characterName && characterName !== playerName && (
                <span className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Joueur :' : 'Player:'} {playerName}
                </span>
              )}
              {completeness !== null && (
                <Badge variant="outline" className="text-xs">
                  {completeness}% {language === 'fr' ? 'complète' : 'complete'}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground font-mono">
                v{sheet.version}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Vue joueur toggle */}
            <Button
              variant={viewAsPlayer ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewAsPlayer(v => !v)}
              title={language === 'fr' ? 'Voir comme joueur (masque les champs MJ)' : 'View as player (hides GM fields)'}
            >
              {viewAsPlayer
                ? <><EyeOff className="h-4 w-4 mr-1" />{language === 'fr' ? 'Vue MJ' : 'GM View'}</>
                : <><Eye    className="h-4 w-4 mr-1" />{language === 'fr' ? 'Vue joueur' : 'Player View'}</>
              }
            </Button>

            {/* Export PDF */}
            {showPdfTab && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPdf}
                disabled={exportingPdf}
              >
                {exportingPdf
                  ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : <Download className="h-4 w-4 mr-1" />}
                {t('pdf_mode_export', language)}
              </Button>
            )}

            {/* Edit / Save / Cancel */}
            {activeTab === 'sheet' && !viewAsPlayer && (
              <>
                {editMode ? (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                      <X className="h-4 w-4 mr-1" />
                      {t('cancel', language)}
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={saving}>
                      {saving
                        ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        : <Save    className="h-4 w-4 mr-1" />}
                      {t('character_sheet_save', language)}
                    </Button>
                  </>
                ) : (
                  <Button size="sm" onClick={() => setEditMode(true)}>
                    <Pencil className="h-4 w-4 mr-1" />
                    {t('character_sheet_edit', language)}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Onglets ─────────────────────────────────────────────────────── */}
        <div className="flex border-b border-border gap-0">
          <button
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'sheet'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('sheet')}
          >
            <ClipboardList className="h-4 w-4" />
            {t('character_sheet', language)}
          </button>

          {showPdfTab && (
            <button
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'pdf'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab('pdf')}
            >
              <FileText className="h-4 w-4" />
              {t('pdf_mode_tab', language)}
            </button>
          )}

          <button
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'history'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('history')}
          >
            <History className="h-4 w-4" />
            {t('history_tab', language)}
          </button>
        </div>

        {/* ── Onglet : Fiche ───────────────────────────────────────────────── */}
        {activeTab === 'sheet' && (
          <div className="space-y-4">
            {viewAsPlayer && (
              <Card className="glass-card border-primary/20">
                <CardContent className="p-3 flex items-center gap-2 text-sm text-primary">
                  <Eye className="h-4 w-4 shrink-0" />
                  {language === 'fr'
                    ? 'Mode lecture — vue joueur (sections MJ masquées, édition désactivée)'
                    : 'Read-only — player view (GM sections hidden, editing disabled)'}
                </CardContent>
              </Card>
            )}

            {visibleSections.map((section, idx) => (
              <SheetSection
                key={section.id}
                section={section}
                schema={template?.fields_schema}
                data={formData}
                onChange={handleFieldChange}
                readOnly={isReadOnly || viewAsPlayer}
                defaultOpen={idx === 0 || !section.collapsed_by_default}
              />
            ))}

            <p className="text-xs text-center text-muted-foreground pb-2">
              {template?.name} v{template?.version}
            </p>
          </div>
        )}

        {/* ── Onglet : PDF ─────────────────────────────────────────────────── */}
        {activeTab === 'pdf' && showPdfTab && (
          <CharacterPDFMode
            campaignId={campaignId}
            sheetId={sheetId}
            sheetVersion={sheet?.version ?? 1}
            pdfAssetId={template.pdf_asset_id}
            pdfOverlay={template.pdf_overlay}
            builderAnnotations={template.builder_annotations || []}
            formData={formData}
            onFormDataChange={viewAsPlayer ? () => {} : setFormData}
            onVersionUpdate={viewAsPlayer ? () => {} : handlePdfVersionUpdate}
            isActive={activeTab === 'pdf'}
            isMJ={true}
            isReadOnly={viewAsPlayer}
            language={language}
          />
        )}

        {/* ── Onglet : Historique ──────────────────────────────────────────── */}
        {activeTab === 'history' && (
          <HistoryTab
            campaignId={campaignId}
            sheetId={sheetId}
            isMJ={true}
            language={language}
          />
        )}

      </div>
    </MainLayout>
  );
}
